/**
 * RuleException Type Definition
 *
 * Represents a rule exception within a reporting period.
 */

export interface RuleException {
  ruleExceptionId: number;
  reportingPeriodId: number;
  ruleKey: string;
  status: string;
  shortDescription: string;
  description: string;
  assignedTo: string;
  riskLevel: "Low" | "Medium" | "High" | "Critical";
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Cash Movement Exception Details
 * Specific details for unexplained cash movement exceptions
 */
export interface CashMovementException extends RuleException {
  ruleKey: "unexplained-cash-movement";
  movementCount?: number;
  totalAmount?: number;
  currency?: string;
  movementDetails?: CashMovementDetail[];
}

export interface CashMovementDetail {
  transactionId?: string;
  date: string;
  amount: number;
  currency: string;
  account?: string;
  description?: string;
  requiresExplanation?: boolean;
}

/**
 * Exception Type Utilities
 */
export const ExceptionUtils = {
  isCashMovementException: (exception: RuleException): exception is CashMovementException => {
    return exception.ruleKey === "unexplained-cash-movement";
  },

  formatCashMovementDescription: (exception: CashMovementException): string => {
    const count = exception.movementCount || 0;
    const amount = exception.totalAmount || 0;
    const currency = exception.currency || "USD";
    
    return `There are ${count} unexplained cash movements totaling ${amount.toLocaleString()} ${currency}. Please review and confirm all cash movements have proper documentation.`;
  },

  getRiskLevelColor: (riskLevel: string): string => {
    switch (riskLevel.toLowerCase()) {
      case "critical": return "text-red-600 bg-red-50";
      case "high": return "text-orange-600 bg-orange-50";
      case "medium": return "text-yellow-600 bg-yellow-50";
      case "low": return "text-blue-600 bg-blue-50";
      default: return "text-gray-600 bg-gray-50";
    }
  },

  getStatusColor: (status: string): string => {
    switch (status.toLowerCase()) {
      case "open": return "text-red-600 bg-red-50";
      case "in progress": return "text-yellow-600 bg-yellow-50";
      case "resolved": return "text-green-600 bg-green-50";
      case "closed": return "text-gray-600 bg-gray-50";
      default: return "text-blue-600 bg-blue-50";
    }
  }
}; 